import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/Routes';
import { formatTimeUntilReset, USAGE_FEATURES } from '../constants/UsageLimits';

const textTitle =
  "font-['Inter'] font-normal text-[16px] leading-[24px] text-neutral-900 dark:text-white";
const textBody =
  "font-['Inter'] font-normal text-[16px] leading-[24px] text-neutral-800 dark:text-white";

const FEATURE_MESSAGES = {
  [USAGE_FEATURES.ANALYTICS_VIEW]: {
    title: "You've reached your Analytics views limit",
    subtitle:
      'Update your plan or wait until it resets. View your usage to see when it resets.',
  },
  [USAGE_FEATURES.BIQ_QUERY]: {
    title: "You've reached your BiQ query limit",
    subtitle:
      'Update your plan or wait until it resets. View your usage to see when it resets.',
  },
  [USAGE_FEATURES.BIQ_DEEP_THINKING]: {
    title: "You've reached your Deep Thinking limit",
    intro: 'No worries — you can continue using BiQ in standard mode.',
    primaryButtonLabel: 'Go back to BiQ',
    body: 'To use Deep Thinking again, upgrade your plan, wait for your limit to reset or buy extra usage.',
  },
};

const btnPrimary =
  'bg-bloom-green hover:bg-bloom-green-hover text-white px-[24px] py-[12px] rounded-[8px] font-[\'Inter\'] font-medium text-[16px] transition-colors';

const UsageBlurOverlay = ({
  feature,
  usage,
  lastReset,
  onClose,
  featureName = 'this feature',
  primaryActionLabel,
  onPrimaryAction,
}) => {
  const navigate = useNavigate();
  const copy = FEATURE_MESSAGES[feature] || {
    title: 'You reached your free tier limit.',
    subtitle: 'Update to another plan or wait until it resets.',
  };

  const resetInText = lastReset ? formatTimeUntilReset(lastReset) : null;

  const handleGoToUsage = () => {
    navigate(ROUTES.USAGE);
  };

  const handleManageSubscription = () => {
    navigate(ROUTES.SUBSCRIPTION);
  };

  const isDeepThinking = feature === USAGE_FEATURES.BIQ_DEEP_THINKING;
  const deepCopy = isDeepThinking ? FEATURE_MESSAGES[USAGE_FEATURES.BIQ_DEEP_THINKING] : null;
  const primaryLabel =
    primaryActionLabel || deepCopy?.primaryButtonLabel;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-neutral-900/[0.12] backdrop-blur-[17.5px] dark:bg-[rgba(214,214,214,0.2)]"
        style={{
          backdropFilter: 'blur(17.5px)',
          WebkitBackdropFilter: 'blur(17.5px)',
        }}
      />

      <div className="relative z-10 flex flex-col items-center text-center gap-[20px] max-w-full">
        {isDeepThinking && deepCopy ? (
          <>
            <div className="flex flex-col gap-[16px] mx-12 sm:mx-16 md:mx-24 lg:mx-32 xl:mx-40">
              <p className={textTitle}>{deepCopy.title}</p>
              <p className={textBody}>{deepCopy.intro}</p>
              {primaryLabel && onPrimaryAction && (
                <div className="flex justify-center">
                  <button type="button" onClick={onPrimaryAction} className={btnPrimary}>
                    {primaryLabel}
                  </button>
                </div>
              )}
              <p className={textBody}>{deepCopy.body}</p>
              {resetInText && (
                <p className={textBody}>
                  Your Deep Thinking limit resets in {resetInText}.
                </p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={handleGoToUsage} className={btnPrimary}>
                Go to usage
              </button>
              <button type="button" onClick={handleManageSubscription} className={btnPrimary}>
                Manage subscription
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-[8px] mx-12 sm:mx-16 md:mx-24 lg:mx-32 xl:mx-40">
              <p className={textTitle}>{copy.title}</p>
              <p className={textBody}>{'subtitle' in copy ? copy.subtitle : ''}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {primaryActionLabel && onPrimaryAction && (
                <button type="button" onClick={onPrimaryAction} className={btnPrimary}>
                  {primaryActionLabel}
                </button>
              )}
              <button type="button" onClick={handleGoToUsage} className={btnPrimary}>
                Go to Usage
              </button>
              <button type="button" onClick={handleManageSubscription} className={btnPrimary}>
                Manage Subscription
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UsageBlurOverlay;
